/**
 * Styled component mapping for MDX doc sections (dark theme).
 * Also exposes doc-specific components (MiniChart, Tag) so .mdx files can use
 * them without imports.
 */

import { useState } from 'react';
import MiniChart from './MiniChart';

// Two typography scales: 'panel' (compact, in-app modal) and 'site' (public
// /docs page: larger, airier, long-form reading).
const SCALES = {
  panel: {
    h1: { fontSize: '24px', fontWeight: 600, color: '#e0e0e0', marginTop: 0, marginBottom: '16px' },
    h2: { fontSize: '18px', fontWeight: 600, color: '#e0e0e0', marginTop: '28px', marginBottom: '12px' },
    h3: { fontSize: '15px', fontWeight: 600, color: '#cbd5e1', marginTop: '20px', marginBottom: '8px' },
    p: { color: '#b0bcc8', lineHeight: 1.7, marginBottom: '16px', fontSize: '14px', maxWidth: '85ch' },
    list: { color: '#b0bcc8', lineHeight: 1.8, paddingLeft: '20px', fontSize: '14px', marginBottom: '16px', maxWidth: '85ch' },
  },
  site: {
    h1: { fontSize: '34px', fontWeight: 700, color: '#f1f5f9', marginTop: 0, marginBottom: '12px', letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '22px', fontWeight: 600, color: '#e2e8f0', marginTop: '44px', marginBottom: '14px', letterSpacing: '-0.01em', scrollMarginTop: '90px' },
    h3: { fontSize: '17px', fontWeight: 600, color: '#cbd5e1', marginTop: '30px', marginBottom: '10px', scrollMarginTop: '90px' },
    p: { color: '#a8b3c4', lineHeight: 1.8, marginBottom: '18px', fontSize: '15.5px' },
    list: { color: '#a8b3c4', lineHeight: 1.9, paddingLeft: '22px', fontSize: '15.5px', marginBottom: '18px' },
  },
};

export function Tag({ color = '#4a9eff', children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '3px',
      fontSize: '0.75rem', fontWeight: 600, color, background: `${color}18`,
      marginRight: '0.5rem',
    }}>
      {children}
    </span>
  );
}

/**
 * Term: an inline concept with an educational hover card.
 * Usage in MDX: <Term info="...explanation...">long gamma</Term>
 */
export function Term({ info, children }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span style={{
        borderBottom: '1px dotted #4a9eff',
        cursor: 'help',
        color: '#cbd5e1',
      }}>
        {children}
      </span>
      {show && (
        <span style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
          width: '320px',
          background: 'rgba(15, 20, 25, 0.97)',
          border: '1px solid #2a3344',
          borderRadius: '6px',
          padding: '10px 12px',
          fontSize: '12px',
          lineHeight: 1.55,
          color: '#b0bcc8',
          fontStyle: 'normal',
          fontWeight: 400,
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
        }}>
          {info}
        </span>
      )}
    </span>
  );
}

export function Callout({ title, children }) {
  return (
    <div style={{
      background: '#0f1419', border: '1px solid #2a3344', borderRadius: '8px',
      padding: '16px', margin: '20px 0',
    }}>
      {title && (
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#4a9eff', marginTop: 0, marginBottom: '8px' }}>
          {title}
        </h3>
      )}
      <div style={{ color: '#b0bcc8', lineHeight: 1.7, fontSize: '14px' }}>
        {children}
      </div>
    </div>
  );
}

export function makeMdxComponents(variant = 'panel') {
  const { h1, h2, h3, p, list } = SCALES[variant] || SCALES.panel;
  return {
  h1: (props) => <h1 style={h1} {...props} />,
  h2: (props) => <h2 style={h2} {...props} />,
  h3: (props) => <h3 style={h3} {...props} />,
  p: (props) => <p style={p} {...props} />,
  ul: (props) => <ul style={list} {...props} />,
  ol: (props) => <ol style={list} {...props} />,
  li: (props) => <li {...props} />,
  strong: (props) => <strong style={{ color: '#e0e0e0' }} {...props} />,
  em: (props) => <em {...props} />,
  a: (props) => <a style={{ color: '#4a9eff' }} target="_blank" rel="noreferrer" {...props} />,
  code: (props) => (
    <code
      style={{
        background: '#0f1419', border: '1px solid #2a3344', borderRadius: '4px',
        padding: '1px 6px', fontSize: '12.5px', color: '#93c5fd',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      }}
      {...props}
    />
  ),
  blockquote: (props) => (
    <blockquote
      style={{
        borderLeft: '3px solid #2a3344', margin: '16px 0', padding: '4px 16px',
        color: '#8a9ab0',
      }}
      {...props}
    />
  ),
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #2a3344', margin: '28px 0' }} />,
  table: (props) => (
    <table style={{ borderCollapse: 'collapse', fontSize: '13px', color: '#b0bcc8', margin: '16px 0' }} {...props} />
  ),
  th: (props) => (
    <th style={{ border: '1px solid #2a3344', padding: '6px 12px', color: '#e0e0e0', textAlign: 'left' }} {...props} />
  ),
  td: (props) => <td style={{ border: '1px solid #2a3344', padding: '6px 12px' }} {...props} />,
  // Doc-specific components available in every .mdx without imports
  MiniChart,
  Tag,
  Callout,
  Term,
  };
}

const mdxComponents = makeMdxComponents('panel');
export default mdxComponents;
